import { useMutation, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api, requestBlob } from "@/lib/api";
import { endpoints, type DocumentEnvelope, type InvoiceDetail } from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/** `GET /invoices/:id` → the document, lines and all. */
export function useInvoice(id: string | undefined): UseQueryResult<InvoiceDetail> {
  return useQuery({
    queryKey: queryKeys.invoices.detail(id ?? ""),
    queryFn: async () => {
      const response = await api.get<DocumentEnvelope<InvoiceDetail>>(endpoints.invoice(id ?? ""));
      return response.data;
    },
    enabled: id !== undefined && id !== "",
    /*
     * An ISSUED invoice is IMMUTABLE (§2.1) — it cannot be edited, only
     * cancelled. So the only thing that can change under this cache is the
     * status, and a cancellation invalidates the key itself. Five minutes is
     * therefore generous rather than risky, and it means re-opening a bill from
     * the register is instant on a workshop connection.
     */
    staleTime: 5 * 60_000,
  });
}

/**
 * The PDF — fetched as BYTES, never navigated to.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY NOT `<a href="/api/v1/invoices/7/pdf">` OR AN `<iframe src=…>`. The PDF
 * route is authenticated with a BEARER token (§1.1 — bearer rather than cookies,
 * so the Capacitor shell works unchanged). A browser navigation sends no
 * Authorization header, so both of those get a 401 and render the API's JSON
 * error inside a PDF viewer. The only way to open it is to fetch it with the
 * header and hand the bytes to the viewer as an object URL.
 *
 * EVERY OBJECT URL IS REVOKED. `URL.createObjectURL` pins the whole blob in
 * memory until it is revoked or the tab closes; a few megabytes per bill, and
 * the owner opens bills all day. The previous one is released before a new one
 * is made, and the last one on unmount.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function useInvoicePdf(id: string | undefined, number: string | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  const release = () => {
    if (urlRef.current !== null) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  };

  // Release on unmount — navigating away from a bill must not leak its bytes.
  useEffect(() => release, []);

  const mutation = useMutation({
    /*
     * §9.2 — the three statutory copies. `copy` absent prints the bill as
     * stored (ORIGINAL); DUPLICATE is the transporter's copy that travels with
     * the goods and TRIPLICATE is the seller's file copy. Same document, same
     * totals — only the caption row changes, which is why this is a query
     * parameter and not a stored field mutation.
     */
    mutationFn: async (copy?: "ORIGINAL" | "DUPLICATE" | "TRIPLICATE") => {
      const suffix = copy === undefined ? "" : `?copy=${copy}`;
      const fallback = `${(number ?? `invoice-${id ?? ""}`).replace(/\//g, "-")}.pdf`;
      return requestBlob(`${endpoints.invoicePdf(id ?? "")}${suffix}`, {
        fallbackFilename: fallback,
      });
    },
    onSuccess: (result) => {
      release();
      const next = URL.createObjectURL(result.blob);
      urlRef.current = next;
      setUrl(next);
      setFilename(result.filename);
    },
  });

  return { ...mutation, url, filename, release };
}

/**
 * `POST /invoices/:id/cancel { reason }` (§17.4).
 *
 * A STATUS CHANGE AND NOTHING ELSE. The number is retained, the lines are
 * retained, the row is never deleted — a number that vanished from a sequential
 * series is the first thing a GST officer asks about. The caller must invalidate
 * both the detail and the register afterwards, and the dashboard too: the home
 * screen's "billed this month" stops counting a cancelled bill.
 */
export function useCancelInvoice(id: string | undefined) {
  return useMutation({
    mutationFn: async (reason: string) => {
      const response = await api.post<DocumentEnvelope<InvoiceDetail>>(
        endpoints.invoiceCancel(id ?? ""),
        { reason },
      );
      return response.data;
    },
  });
}
